"use client";

import React from "react";
import { DetailDrawer, DetailDrawerSkeleton } from "@/components/ui/detail-drawer";
import { Text } from "@/components/ui/text";
import { Separator } from "@/components/ui/separator";
import { MeetingsService } from "@/api-client/meetings";
import { useAuthenticatedQuery } from "@/hooks/use-authenticated-query";
import { Sparkles, CheckCircle2, Lightbulb, ArrowRight, ListChecks } from "lucide-react";
import { useRouter } from "next/navigation";
import { ApiErrorPanel } from "@/components/ui/api-error-panel";
import { getApiErrorMessage } from "@/lib/api-error-message";

interface MeetingDetailDrawerProps {
  meetingId: number | null;
  scrollToSection?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MeetingDetailDrawer: React.FC<MeetingDetailDrawerProps> = ({
  meetingId,
  scrollToSection,
  open,
  onOpenChange,
}) => {
  const router = useRouter();
  const { data, isLoading, error } = useAuthenticatedQuery<{
    highlights: string[];
    next_steps: string[];
    action_items: string[];
    insights: string[];
  }>({
    queryKey: ["callDetails", meetingId],
    queryFn: () => {
      if (!meetingId) throw new Error("Meeting ID is required");
      return MeetingsService.getCallDetails(meetingId);
    },
    enabled: open && meetingId !== null,
    staleTime: 5 * 60 * 1000,
  });

  React.useEffect(() => {
    if (open && scrollToSection && data) {
      const timer = setTimeout(() => {
        const element = document.getElementById(scrollToSection);
        if (element) {
          const scrollContainer = element.closest('[class*="overflow-y-auto"]') as HTMLElement;
          
          if (scrollContainer) {
            const containerRect = scrollContainer.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const scrollOffset = elementRect.top - containerRect.top + scrollContainer.scrollTop - 24;
            
            scrollContainer.scrollTo({
              top: Math.max(0, scrollOffset),
              behavior: "smooth"
            });
          } else {
            const offset = 24;
            const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({
              top: elementTop - offset,
              behavior: "smooth"
            });
          }
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open, scrollToSection, data]);

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Call Details"
      side="right"
    >
      {isLoading && <DetailDrawerSkeleton />}
      
      {error ? (
        <ApiErrorPanel
          className="min-h-0 py-8"
          message={getApiErrorMessage(error)}
          onTryAgain={() => window.location.reload()}
          onGoHome={() => router.push("/")}
        />
      ) : null}

      {!isLoading && data ? (
        <div className="space-y-6">
          {data.highlights && data.highlights.length > 0 && (
            <div id="highlights-section" className="space-y-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <Text as="h4" size="base" weight="semibold" color="default">
                  Highlights
                </Text>
              </div>
              <div className="space-y-3 pl-7">
                {data.highlights.map((highlight, index) => (
                  <div
                    key={index}
                    className="relative pl-4 before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:rounded-full before:bg-primary"
                  >
                    <Text
                      size="sm"
                      weight="normal"
                      color="default"
                      className="leading-relaxed text-muted-foreground"
                    >
                      {highlight}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.next_steps && data.next_steps.length > 0 && (
            <>
              <Separator className="my-6" />
              <div id="next-steps-section" className="space-y-4" >
                <div className="flex items-center gap-2">
                  <ArrowRight className="w-5 h-5 text-primary" />
                  <Text as="h4" size="base" weight="semibold" color="default">
                    Next Steps
                  </Text>
                </div>
                <div className="space-y-3 pl-7">
                  {data.next_steps.map((step, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 border border-border/50 hover:bg-secondary/70 transition-colors"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-5 h-5 rounded-full bg-primary/30 flex items-center justify-center">
                          <Text size="xs" weight="semibold" color="primary">
                            {index + 1}
                          </Text>
                        </div>
                      </div>
                      <Text
                        size="sm"
                        weight="normal"
                        color="default"
                        className="leading-relaxed flex-1"
                      >
                        {step}
                      </Text>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {data.action_items && data.action_items.length > 0 && (
            <>
              <Separator className="my-6" />
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <ListChecks className="w-5 h-5 text-primary" />
                  <Text as="h4" size="base" weight="semibold" color="default">
                    Action Items
                  </Text>
                </div>
                <div className="space-y-2.5 pl-7">
                  {data.action_items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center  gap-3 p-3 rounded-lg bg-card border border-border hover:border-primary/20 transition-all"
                    >
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <Text
                        size="sm"
                        weight="normal"
                        color="default"
                        className="leading-relaxed flex-1"
                      >
                        {item}
                      </Text>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {data.insights && data.insights.length > 0 && (
            <>
              <Separator className="my-6" />
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-primary" />
                  <Text as="h4" size="base" weight="semibold" color="default">
                    Insights
                  </Text>
                </div>
                <div className="grid gap-3 pl-7">
                  {data.insights.map((insight, index) => (
                    <div
                      key={index}
                      className="p-4 rounded-lg bg-primary/5 border border-primary/10 hover:bg-primary/10 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                      
                        <Text
                          size="sm"
                          weight="normal"
                          color="default"
                          className="leading-relaxed flex-1"
                        >
                          {insight}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {(!data.highlights || data.highlights.length === 0) &&
            (!data.next_steps || data.next_steps.length === 0) &&
            (!data.action_items || data.action_items.length === 0) &&
            (!data.insights || data.insights.length === 0) && (
              <div className="flex flex-col items-center justify-center py-12">
                <Text size="base" weight="medium" color="muted">
                  No call details available.
                </Text>
              </div>
            )}
        </div>
      ) : null}
    </DetailDrawer>
  );
};

